import { useRouter } from 'expo-router';
import { useMemo } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';

import { Fonts, Palette, Spacing } from '@/constants/theme';
import type { ActiveTask } from '@/lib/tasks';

/**
 * Horizontal day strip above "Your one thing." Each cell is one day with
 * up to three forest dots showing task count. Day count and cell width
 * are responsive — phone shows ~14 days at a tight 38px, desktop fills
 * the wider column with 21 days that flex-grow into the space.
 *
 * Tapping any day routes to Schedule. Overdue tasks (due_at <
 * start-of-today) collapse into a leading "OVERDUE · N" chip so a
 * backlog is visible without horizontal scrolling.
 */

export function CalendarPeek({ tasks }: { tasks: ActiveTask[] }) {
  const router = useRouter();
  const { width: viewportWidth } = useWindowDimensions();
  // More days on wider viewports — keeps the strip from looking sparse on
  // desktop. Threshold matches the column-bump in _layout.tsx.
  const daysAhead = viewportWidth >= 1024 ? 21 : viewportWidth >= 700 ? 17 : 14;

  const { overdue, days } = useMemo(() => buildDays(tasks, daysAhead), [tasks, daysAhead]);

  const goSchedule = () => router.push('/(tabs)/schedule' as never);
  // The overdue chip should land users in Schedule with the overdue
  // filter pre-applied, not the full list. We use navigate (not push)
  // for the same reason day-jumps do — already-mounted tab + query-
  // string form re-triggers useLocalSearchParams reliably on every
  // platform.
  const goScheduleOverdue = () =>
    router.navigate('/(tabs)/schedule?overdue=1' as never);
  // Tapping a specific day takes the user to Schedule with a ?date= query
  // so Schedule can scroll/highlight that day. The format matches the
  // bucket keys built in buildDays (YYYY-MM-DD).
  //
  // We use a URL-string instead of `{ pathname, params }` because on
  // native, navigating to an already-mounted tab via the object form
  // doesn't always re-trigger useLocalSearchParams — the query-string
  // form goes through the URL parser and updates params consistently
  // on both web and iOS / Android.
  const goScheduleOnDay = (dateKey: string) =>
    router.navigate(`/(tabs)/schedule?date=${encodeURIComponent(dateKey)}` as never);

  return (
    <View style={styles.wrap}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.row}>
        {overdue > 0 ? (
          <Pressable
            onPress={goScheduleOverdue}
            accessibilityRole="button"
            accessibilityLabel={`Open Schedule filtered to ${overdue} overdue task${overdue === 1 ? '' : 's'}`}
            style={({ pressed }) => [styles.overdue, pressed && { opacity: 0.85 }]}>
            <Text style={styles.overdueCount}>{overdue}</Text>
            <Text style={styles.overdueLabel}>OVERDUE</Text>
          </Pressable>
        ) : null}
        {days.map((d) => (
          <Pressable
            key={d.key}
            onPress={() => goScheduleOnDay(d.key)}
            style={({ pressed }) => [
              styles.day,
              d.isToday && styles.dayToday,
              pressed && { opacity: 0.88 },
            ]}>
            <Text style={[styles.dayLetter, d.isToday && styles.dayLetterToday]}>
              {d.weekday}
            </Text>
            <Text style={[styles.dayNum, d.isToday && styles.dayNumToday]}>
              {d.day}
            </Text>
            <View style={styles.dots}>
              {Array.from({ length: Math.min(3, d.count) }).map((_, i) => (
                <View key={i} style={[styles.dot, d.isToday && styles.dotToday]} />
              ))}
              {d.count > 3 ? (
                <Text style={[styles.more, d.isToday && { color: Palette.cream }]}>
                  +{d.count - 3}
                </Text>
              ) : null}
            </View>
          </Pressable>
        ))}
      </ScrollView>
    </View>
  );
}

interface DayCell {
  key: string;
  weekday: string;
  day: number;
  count: number;
  isToday: boolean;
}

function buildDays(tasks: ActiveTask[], daysAhead: number): { overdue: number; days: DayCell[] } {
  const out: DayCell[] = [];
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  const todayKey = ymd(startOfToday);

  // Bucket tasks by YYYY-MM-DD, count overdue separately.
  const buckets = new Map<string, number>();
  let overdue = 0;
  for (const t of tasks) {
    if (t.due_at == null) continue;
    if (t.due_at < startOfToday) {
      overdue += 1;
      continue;
    }
    const key = ymd(t.due_at);
    buckets.set(key, (buckets.get(key) ?? 0) + 1);
  }

  for (let i = 0; i < daysAhead; i++) {
    const d = new Date(startOfToday + i * dayMs);
    const key = ymd(d.getTime());
    out.push({
      key,
      weekday: d.toLocaleDateString(undefined, { weekday: 'narrow' }),
      day: d.getDate(),
      count: buckets.get(key) ?? 0,
      isToday: key === todayKey,
    });
  }

  return { overdue, days: out };
}

function ymd(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const styles = StyleSheet.create({
  wrap: {
    marginTop: Spacing.three,
  },
  row: {
    paddingHorizontal: Spacing.four,
    gap: 5,
    alignItems: 'center',
    // Fill the available width — cells flex-grow into whatever room is
    // left after the overdue chip + padding. Without this, the strip
    // left-aligned and we got dead white space on the right at desktop.
    flexGrow: 1,
  },
  day: {
    // flexBasis:0 + flexGrow:1 distributes available width equally
    // across all day cells regardless of viewport.
    flexBasis: 0,
    flexGrow: 1,
    minWidth: 36,
    paddingVertical: 8,
    borderRadius: 12,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  dayToday: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },
  dayLetter: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.inkFaint,
    letterSpacing: 0.4,
  },
  dayLetterToday: { color: 'rgba(251,246,234,0.75)' },
  dayNum: {
    fontFamily: Fonts.serifBold,
    fontSize: 17,
    color: Palette.ink,
    letterSpacing: -0.3,
  },
  dayNumToday: { color: Palette.cream },
  dots: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    height: 8,
    marginTop: 1,
  },
  dot: {
    width: 4, height: 4, borderRadius: 2, backgroundColor: Palette.forest,
  },
  dotToday: { backgroundColor: Palette.honey },
  more: {
    fontFamily: Fonts.sansBold,
    fontSize: 8.5,
    color: Palette.forest,
    marginLeft: 1,
  },
  overdue: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 12,
    backgroundColor: 'rgba(176,95,60,0.15)',
    borderWidth: 1,
    borderColor: 'rgba(176,95,60,0.35)',
    alignItems: 'center',
    minWidth: 56,
  },
  overdueCount: {
    fontFamily: Fonts.serifBold,
    fontSize: 18,
    color: Palette.clay,
    letterSpacing: -0.3,
  },
  overdueLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 8.5,
    color: Palette.clay,
    letterSpacing: 0.5,
    marginTop: 1,
  },
});
